import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { onboardingRuns } from "../../api/endpoints/onboardingRuns";
import { qk } from "../../api/queryKeys";
import { RUN_STATUSES, type RunListFilters, type RunStatus } from "../../api/types/onboardingRun";
import { SelectField } from "../../components/forms/SelectField";
import { TextField } from "../../components/forms/TextField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { EmptyState } from "../../components/ui/EmptyState";
import { StatusBadge } from "./StatusBadge";

const STATUS_OPTIONS = [{ value: "", label: "— any —" }, ...RUN_STATUSES];

export function RunList({
  selectedRun,
  onSelect,
}: {
  selectedRun: string | null;
  onSelect: (id: string) => void;
}) {
  const [status, setStatus] = useState<string>("");
  const [tenant, setTenant] = useState("");
  const [site, setSite] = useState("");
  const filters: RunListFilters = {
    status: status ? (status as RunStatus) : undefined,
    tenant_slug: tenant || undefined,
    site_slug: site || undefined,
  };
  const q = useQuery({
    queryKey: qk.onboardingRuns(filters),
    queryFn: () => onboardingRuns.list(filters).then((r) => r.data),
  });

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
        <SelectField label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        <TextField label="Tenant slug" value={tenant} onChange={setTenant} placeholder="any" />
        <TextField label="Site slug" value={site} onChange={setSite} placeholder="any" />
      </div>

      {q.error && <ErrorBanner error={q.error} title="Failed to load runs" />}
      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading runs…</p>}
      {q.data && q.data.length === 0 && (
        <EmptyState title="No onboarding runs yet" description="Create one from the run console (coming in F3)." />
      )}
      {q.data && q.data.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--text-dim)" }}>
              <th style={th}>Status</th><th style={th}>Run type</th><th style={th}>Scope</th><th style={th}>Created</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => (
              <tr
                key={r.id}
                onClick={() => onSelect(r.id)}
                style={{
                  cursor: "pointer",
                  background: r.id === selectedRun ? "var(--surface-2)" : undefined,
                }}
              >
                <td style={td}><StatusBadge status={r.status} /></td>
                <td style={td}>{r.run_type}</td>
                <td style={td}><code>{r.lock_scope}:{r.lock_key}</code></td>
                <td style={td}>{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: "6px 8px", borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "6px 8px", borderBottom: "1px solid var(--border)" };
